import Service from 'react-reservice';

import symbolsAPI, { ISymbol } from '@webtrader/core/api/market/symbols';
import symbolDetailAPI, { ISymbolDetail } from '@webtrader/core/api/market/symbols/detail';
import symbolOrderDetailAPI from '@webtrader/core/api/market/symbols/order';
import watchlistAPI, { EWatchlistFilter } from '@webtrader/core/api/market/watchlist';
import { calculatePriceDifference } from '@webtrader/core/calculation/price';
import { EMarketFilter, TSymbolFilter } from '@webtrader/core/constants/symbol';
import orderBy, { SortOrder } from '@webtrader/core/format/orderBy';
import { filterSymbolsByMarketFilter } from '@webtrader/core/format/symbol';
import { IApiError } from '@webtrader/core/request/api';
import { mapResponseCodeToMessage } from '@webtrader/core/request/message';
import { chartService } from '@webtrader/core/services/chart';
import { companyService } from '@webtrader/core/services/company';
import { newOrderService } from '@webtrader/core/services/form/order/new';
import { marketService } from '@webtrader/core/services/market';
import { orderService } from '@webtrader/core/services/order';
import { symbolsPreferenceService } from '@webtrader/core/services/symbols/preference';
import { userPreferenceService } from '@webtrader/core/services/userPreference';

export enum ESortFilter {
  ONLINE = 'online',
  OPEN = 'open',
  PERCENTAGE_DIFFERENCE = 'percentageDifference',
  SYMBOL = 'symbol',
  ASK = 'ask',
  BID = 'bid',
  SPREAD = 'spread'
}

interface ISymbolsContext {
  sortBy: ESortFilter;
  symbols: ISymbol[];
  allSymbols: ISymbol[];
  loading: boolean;
  showChart: boolean;
  loadingDetail: boolean;
  interactableSymbols: ISymbol[];
  isAscendingSortOrder: boolean;
  selectedWatchListFilter?: EWatchlistFilter;
  selectedMarketFilter?: EMarketFilter;
  selectedSymbol?: ISymbol;
  selectedSymbolDetail?: ISymbolDetail;
}

interface IFilterSymbolByGroupParams {
  filter: TSymbolFilter;
  selectDefault?: boolean;
  useAllSymbolsList?: boolean;
}

interface IFetchMarketSymbolsParams {
  filter: EMarketFilter;
  selectDefault?: boolean;
}

interface IFetchWatchlistSymbolsParams {
  filter: EWatchlistFilter;
  selectDefault?: boolean;
}

interface ISymbolOrderDetailParams {
  symbol: string;
  onComplete?: (result: ISymbolDetail) => void;
}

const defaultContext: ISymbolsContext = {
  sortBy: ESortFilter.ONLINE,
  symbols: [],
  allSymbols: [],
  showChart: true,
  loading: true,
  loadingDetail: true,
  interactableSymbols: [],
  isAscendingSortOrder: false,
  selectedWatchListFilter: undefined,
  selectedMarketFilter: undefined
};

class SymbolsService extends Service<ISymbolsContext> {
  private preventSelectDefault = false;

  constructor() {
    super();

    this.context = {
      ...defaultContext
    };
  }

  public init = async (defaultFilter: TSymbolFilter) => {
    const { preventSelectDefault } = this;

    if (!preventSelectDefault) {
      this.fetchSymbols(defaultFilter, true);

      this.preventSelectDefault = false;
    }
  };

  public initTrade = async () => {
    const { preventSelectDefault } = this;

    if (!preventSelectDefault) {
      // For first time, get Favourite list
      let newWatchListFilter = this.context.selectedWatchListFilter ?? EWatchlistFilter.FAVOURITES;
      await this.fetchSymbols(newWatchListFilter, true);
      const { interactableSymbols } = this.context;

      // If Favourite list is empty, need fallback to Top Gainers list
      if (newWatchListFilter === EWatchlistFilter.FAVOURITES && interactableSymbols.length === 0) {
        newWatchListFilter = this.context.selectedWatchListFilter ?? this.getDefaultWatchListFilter();

        await this.fetchSymbols(newWatchListFilter, true);

        const { interactableSymbols } = this.context;
        if (interactableSymbols.length === 0) {
          // If Top Gainers list is empty, need fallback to All symbol list
          newWatchListFilter = EWatchlistFilter.ALL;
          await this.fetchSymbols(newWatchListFilter, true);
        }
      }

      this.context.selectedWatchListFilter = newWatchListFilter;
      this.preventSelectDefault = false;
    }
  };

  public initMarket = async () => {
    await this.fetchAllMarketSymbols();
  };

  private getDefaultWatchListFilter = () => {
    const { topGainer, topPick, topLoser } = companyService.context.access;
    if (topGainer) {
      return EWatchlistFilter.TOP_GAINER;
    } else if (topPick) {
      return EWatchlistFilter.POPULAR;
    } else if (topLoser) {
      return EWatchlistFilter.TOP_LOSER;
    } else {
      return EWatchlistFilter.ALL;
    }
  };

  public refetch = async (isMarket: boolean) => {
    const { selectedMarketFilter, selectedWatchListFilter } = this.context;

    if (isMarket && selectedMarketFilter) {
      this.fetchSymbols(selectedMarketFilter, true);
    } else if (!isMarket && selectedWatchListFilter) {
      this.fetchSymbols(selectedWatchListFilter, true);
    }
  };

  public reset = () => {
    this.context.selectedSymbol = undefined;
    this.context.sortBy = defaultContext.sortBy;
    this.context.symbols = defaultContext.symbols;
    this.context.allSymbols = defaultContext.allSymbols;
    this.context.loading = defaultContext.loading;
    this.context.showChart = defaultContext.showChart;
    this.context.loadingDetail = defaultContext.loadingDetail;
    this.context.interactableSymbols = defaultContext.interactableSymbols;
    this.context.isAscendingSortOrder = defaultContext.isAscendingSortOrder;
    this.preventSelectDefault = false;
  };

  public resetSelectedWatchListFilter = () => {
    this.context.selectedWatchListFilter = defaultContext.selectedWatchListFilter;
  };

  public resetSelectedMarketFilter = () => {
    this.context.selectedMarketFilter = defaultContext.selectedMarketFilter;
  };

  public setSelectedMarketFilter = (filter: EMarketFilter) => {
    this.context.selectedMarketFilter = filter;
  };

  public resetSelectDefault = () => {
    this.preventSelectDefault = false;
  };

  public preventSelectDefaultOnInit = () => {
    this.preventSelectDefault = true;
  };

  public onSelectSymbol = async (symbol: ISymbol) => {
    if (this.context.selectedSymbol?.symbol !== symbol.symbol) {
      chartService.cleanOrderTrackers(symbol.symbol);
      this.context.selectedSymbol = symbol;
      await this.fetchSymbolDetail(symbol);
      newOrderService.doResetFormData();
    }
  };

  public setSelectedSymbol = (symbol: ISymbol) => {
    this.context.selectedSymbol = symbol;
  };

  public onSelectSymbolOrderDetail = async ({ symbol, onComplete }: ISymbolOrderDetailParams) => {
    try {
      const { result } = await symbolOrderDetailAPI({ symbol });

      if (result) {
        this.onSelectSymbol(result);
        onComplete?.(result);
      }
    } catch {}
  };

  public onSelectSymbolFromOrder = (symbol: string) => {
    this.fetchSymbolDetailForOrder(symbol).then(() => {
      if (this.context.selectedWatchListFilter !== EWatchlistFilter.ALL) {
        this.onSelectWatchlistAllFilter(EWatchlistFilter.ALL, false);
      }
    });
  };

  public onSelectSymbolFromSearch = (symbol: ISymbol) => {
    if (this.context.selectedSymbol?.symbol !== symbol.symbol) {
      this.onSelectSymbol(symbol);
      if (this.context.selectedWatchListFilter !== EWatchlistFilter.ALL) {
        this.onSelectWatchlistAllFilter(EWatchlistFilter.ALL, false);
      }
    }
  };

  public onSelectWatchlistAllFilter = async (filter: EWatchlistFilter, selectDefault = true) => {
    this.context.selectedWatchListFilter = filter;

    await Promise.all([marketService.initMarketSymbolCategories(), this.fetchAllMarketSymbols()]);
    this.filterMarketAllSymbols({ filter, selectDefault });
  };

  public onSelectMarketFilter = (filter: EMarketFilter, selectDefault = true, useAllSymbolsList = false) => {
    this.setSelectedMarketFilter(filter);

    this.filterMarketSymbols({
      filter,
      selectDefault,
      useAllSymbolsList
    });
  };

  public onSelectWatchlistFilter = (filter: EWatchlistFilter, selectDefault = true) => {
    this.context.selectedWatchListFilter = filter;

    this.fetchWatchlistSymbols({
      filter,
      selectDefault
    });
  };

  public onRemoveSymbolWatch = (symbol: string) => {
    watchlistAPI.delete({
      symbol
    });

    this.context.symbols = this.context.symbols.map((s) => {
      if (s.symbol === symbol) {
        return {
          ...s,
          watch: false
        };
      }

      return s;
    });

    this.context.interactableSymbols = this.context.interactableSymbols.filter((interactableSymbol) => {
      return interactableSymbol.symbol !== symbol;
    });
  };

  public updateAllSymbolListForWatch = (symbol: string, watch: boolean) => {
    try {
      const { symbols, interactableSymbols, selectedSymbolDetail } = this.context;

      this.context.symbols = symbols.map((s) => {
        if (s.symbol === symbol) {
          return {
            ...s,
            watch
          };
        }

        return s;
      });

      this.context.interactableSymbols = interactableSymbols.map((s) => {
        if (s.symbol === symbol) {
          return {
            ...s,
            watch
          };
        }

        return s;
      });

      if (selectedSymbolDetail && selectedSymbolDetail.symbol === symbol) {
        this.context.selectedSymbolDetail = {
          ...selectedSymbolDetail,
          watch
        };
      }
    } catch (error) {}
  };

  public switchChartVisibility = () => {
    this.context.showChart = !this.context.showChart;
  };

  public onSetSortBy = (sortBy: ESortFilter) => {
    this.context.sortBy = sortBy;
  };

  public onSetIsAscendingSortOrder = () => {
    this.context.isAscendingSortOrder = !this.context.isAscendingSortOrder;
  };

  private fetchSymbols = async (filter: TSymbolFilter, selectDefault?: boolean) => {
    switch (filter) {
      case EWatchlistFilter.POPULAR:
      case EWatchlistFilter.TOP_LOSER:
      case EWatchlistFilter.TOP_GAINER:
        return this.fetchWatchlistSymbols({ filter, selectDefault });
      case EWatchlistFilter.FAVOURITES:
        return this.fetchWatchlistSymbols({ filter: EWatchlistFilter.FAVOURITES, selectDefault });
      case EWatchlistFilter.ALL:
        return this.onSelectWatchlistAllFilter(filter, selectDefault);
      default:
        return this.fetchMarketSymbols({ filter: filter as EMarketFilter, selectDefault });
    }
  };

  private fetchAllMarketSymbols = async () => {
    this.context.loading = true;

    try {
      const { result: symbols } = await symbolsAPI.allSymbols();

      this.context.allSymbols = symbols || [];

      if (symbols) {
        symbolsPreferenceService.setCheckedSymbols(symbols);
      }
    } catch {
    } finally {
      this.context.loading = false;
    }
  };

  private fetchMarketSymbols = async (params: IFetchMarketSymbolsParams) => {
    const { filter, selectDefault } = params;

    this.context.loading = true;

    try {
      const { result: symbols } = await symbolsAPI.symbols();

      this.context.symbols = symbols || [];

      if (symbols) {
        symbolsPreferenceService.setCheckedSymbols(symbols);
      }

      this.filterMarketSymbols({
        filter,
        selectDefault
      });
    } catch {
    } finally {
      this.context.loading = false;
    }
  };

  private fetchWatchlistSymbols = async (params: IFetchWatchlistSymbolsParams) => {
    const { selectedSymbol } = this.context;

    const { filter, selectDefault } = params;

    this.context.loading = true;

    try {
      const { result: symbols } = await watchlistAPI.items({
        code: filter
      });

      this.context.symbols = symbols || [];

      switch (filter) {
        case EWatchlistFilter.TOP_LOSER:
        case EWatchlistFilter.TOP_GAINER:
          this.context.interactableSymbols = this.sortSymbolsByPercentageDifference(
            symbols || [],
            filter === EWatchlistFilter.TOP_LOSER ? 'asc' : 'desc'
          );
          break;
        default:
          this.context.interactableSymbols = [...(symbols || [])];
          break;
      }

      if (selectDefault && this.context.interactableSymbols.length > 0) {
        const firstSymbol = this.context.interactableSymbols[0];

        this.onSelectSymbol(firstSymbol);
      } else if (selectedSymbol) {
        this.fetchSymbolDetail(selectedSymbol);
      }
    } catch {
    } finally {
      this.context.loading = false;
    }
  };

  private filterMarketAllSymbols = (params: IFilterSymbolByGroupParams) => {
    const { allSymbols, selectedSymbol, selectedMarketFilter = EMarketFilter.ALL } = this.context;

    const { selectDefault } = params;

    if (allSymbols.length > 0) {
      const filteredSymbols = filterSymbolsByMarketFilter(selectedMarketFilter, allSymbols);

      this.context.interactableSymbols = this.sortSymbolsById(filteredSymbols);

      if (selectDefault && this.context.interactableSymbols.length > 0) {
        const firstSymbol = this.context.interactableSymbols[0];

        this.onSelectSymbol(firstSymbol);
      } else if (selectedSymbol) {
        this.fetchSymbolDetail(selectedSymbol);
      }
    }
  };

  private filterMarketSymbols = (params: IFilterSymbolByGroupParams) => {
    const { allSymbols, symbols } = this.context;

    const { filter, useAllSymbolsList } = params;

    const list = useAllSymbolsList ? allSymbols : symbols;

    if (list.length > 0) {
      const filteredSymbols = filterSymbolsByMarketFilter(filter, list);

      if (filter === EMarketFilter.FAVOURITES) {
        this.context.interactableSymbols = filteredSymbols;
      } else {
        this.context.interactableSymbols = this.sortSymbolsById(filteredSymbols);
      }
    }
  };

  private fetchSymbolDetail = async (symbol: ISymbol) => {
    try {
      const { symbol: id, decimal, description } = symbol;

      chartService.onSelectSymbol(id, decimal, {
        description
      });

      this.context.loadingDetail = true;

      userPreferenceService.getLineDrawingUserPreference(id);
      const { result } = await symbolDetailAPI({
        symbol: id
      });

      if (result) {
        this.context.selectedSymbolDetail = result;

        newOrderService.init(result);
        orderService.setSymbolDetail(result);
      }
    } catch (err) {
      const { code, message } = err as IApiError;
      this.reset();
      this.initTrade();

      throw new Error(mapResponseCodeToMessage(code, message));
    } finally {
      this.context.loadingDetail = false;
    }
  };

  private fetchSymbolDetailForOrder = async (symbol: string) => {
    try {
      this.context.loadingDetail = true;

      const { result } = await symbolDetailAPI({
        symbol
      });

      if (result) {
        const { decimal } = result;

        chartService.onSelectSymbol(symbol, decimal);

        this.context.selectedSymbol = result;
        this.context.selectedSymbolDetail = result;

        newOrderService.init(result);
        orderService.setSymbolDetail(result);
      }
    } finally {
      this.context.loadingDetail = false;
    }
  };

  private sortSymbolsById = (symbols: ISymbol[]) => {
    return orderBy(symbols, 'symbol');
  };

  private sortSymbolsByPercentageDifference = (symbols: ISymbol[], order: SortOrder = 'desc') => {
    const calculatedSymbols = symbols.map((symbol) => {
      const { bid, open } = symbol;

      const { percentageDifference } = calculatePriceDifference({
        openPrice: open,
        currentPrice: bid
      });

      return {
        symbol,
        percentageDiff: percentageDifference
      };
    });

    return orderBy(calculatedSymbols, 'percentageDiff', order).map((s) => s.symbol);
  };
}

export default new SymbolsService();
